import { bearing as turfBearing } from "@turf/bearing";
import { distance as turfDistance } from "@turf/distance";
import { point } from "@turf/helpers";
import type { LineString, MultiLineString, Polygon, Position } from "geojson";

export const AB_LINE_ANGLE_TOLERANCE_DEGREES = 5;
export const AB_LINE_MIN_LENGTH_METERS = 50;
export const AB_LINE_SELECTION_MAX_DISTANCE_METERS = 50;
export const AB_LINE_MAX_SEGMENTS = 16;

const headingFormatter = new Intl.NumberFormat("en-US", {
	maximumFractionDigits: 2,
});

export interface FieldSegment {
	heading: number; // 0-180 degrees exclusive (normalized row orientation)
	length: number; // total length in meters
	startPoint: Position; // [lon, lat]
	endPoint: Position; // [lon, lat]
}

interface SegmentGroup {
	startPoint: Position;
	endPoint: Position;
	openingBearing: number; // bearing of first mini-segment, -180 to 180
	length: number;
}

function angleDiff(a: number, b: number): number {
	let diff = ((b - a + 180) % 360) - 180;
	if (diff < -180) diff += 360;
	return Math.abs(diff);
}

function preciseHeadingFromBearing(bearing: number): number {
	const normalized = normalizeBearing(bearing);
	return normalized >= 180 ? normalized - 180 : normalized;
}

function normalizeBearing(bearing: number): number {
	return (bearing + 360) % 360;
}

/**
 * Converts a raw turf bearing (-180..180) to a normalized row-orientation
 * heading in the range [0, 180). Opposite directions map to the same heading
 * since a tillage row has no preferred end.
 */
export function headingFromBearing(bearing: number): number {
	return preciseHeadingFromBearing(bearing);
}

export function formatHeading(heading: number): string {
	return headingFormatter.format(heading);
}

/**
 * Derives the row-orientation heading [0, 180) from a LineString or
 * MultiLineString by computing the bearing from the first to the last
 * coordinate of the geometry.
 */
export function headingFromLineGeometry(
	geometry: LineString | MultiLineString,
): number {
	const bearing = bearingFromLineGeometry(geometry);
	return headingFromBearing(bearing);
}

/**
 * Derives the directional heading [0, 360) from a LineString or
 * MultiLineString by computing the bearing from the first to the last
 * coordinate of the geometry.
 */
export function bearingFromLineGeometry(
	geometry: LineString | MultiLineString,
): number {
	const coords: Position[] =
		geometry.type === "LineString"
			? geometry.coordinates
			: geometry.coordinates.flat();
	if (coords.length < 2) return 0;
	return normalizeBearing(
		turfBearing(point(coords[0]), point(coords[coords.length - 1])),
	);
}

export interface ExtractStraightSegmentsOptions {
	angleTolerance?: number;
	minLengthMeters?: number;
}

export function extractStraightSegments(
	polygon: Polygon,
	options: ExtractStraightSegmentsOptions = {},
): FieldSegment[] {
	const {
		angleTolerance = AB_LINE_ANGLE_TOLERANCE_DEGREES,
		minLengthMeters = AB_LINE_MIN_LENGTH_METERS,
	} = options;

	const coords = polygon.coordinates[0];
	if (coords.length < 3) return [];

	const groups: SegmentGroup[] = [];
	let current: SegmentGroup | null = null;

	for (let i = 0; i < coords.length - 1; i++) {
		const from = coords[i];
		const to = coords[i + 1];

		const segBearing = turfBearing(point(from), point(to));
		const segLength = turfDistance(point(from), point(to), {
			units: "meters",
		});

		if (segLength < 0.01) continue; // skip degenerate zero-length edges

		if (current === null) {
			current = {
				startPoint: from,
				endPoint: to,
				openingBearing: segBearing,
				length: segLength,
			};
		} else {
			// Check if this mini-segment is consistent with the current group
			if (
				angleDiff(current.openingBearing, segBearing) <= angleTolerance
			) {
				current.endPoint = to;
				current.length += segLength;
			} else {
				groups.push(current);
				current = {
					startPoint: from,
					endPoint: to,
					openingBearing: segBearing,
					length: segLength,
				};
			}
		}
	}
	if (current !== null) groups.push(current);

	// Filter by minimum length
	const filtered = groups.filter(g => g.length >= minLengthMeters);

	// Compute final heading for each group (bearing from actual start to end)
	const segments: FieldSegment[] = filtered.map(g => {
		const finalBearing = turfBearing(
			point(g.startPoint),
			point(g.endPoint),
		);
		const heading = headingFromBearing(finalBearing);
		return {
			heading,
			length: g.length,
			startPoint: g.startPoint,
			endPoint: g.endPoint,
		};
	});

	return segments
		.sort((a, b) => b.length - a.length)
		.slice(0, AB_LINE_MAX_SEGMENTS);
}
